import type { ContentType, ToolResultData } from './types';
import { getToolConfig } from '../tool-config';
import {
  isValidJSON,
  isXMLLike,
  isHTML,
  isMarkdown,
  isCode,
  isEndpointsXML,
  isEndpointDetailsXML,
  isFindingsData,
  isFileWriteData,
  isFileReadData,
} from '../content-utils';

export {
  isValidJSON,
  isXMLLike,
  isHTML,
  isMarkdown,
  isCode,
  isEndpointsXML,
  isEndpointDetailsXML,
  isFindingsData,
  isFileWriteData,
  isFileReadData,
  isSignalsData,
} from '../content-utils';

export {
  parseFindingData,
  parseFindingsListData,
  parseFindingsSearchData,
  parseEndpointFromXML,
  parseEndpointsListFromXML,
  parseSignalsListData,
  parseSignalsSearchData,
  parseSignalData,
} from '../content-utils';

const detectContentFromValue = (content: unknown): ContentType => {
  if (typeof content === 'object' && content !== null) {
    if (isFileWriteData(content)) return 'file-write';
    if (isFileReadData(content)) return 'file-read';
    if (isFindingsData(content)) return 'finding';
    return 'json';
  }

  const strContent = String(content);

  if (isHTML(strContent)) return 'html';
  if (isEndpointsXML(strContent)) return 'endpoints-list';
  if (isEndpointDetailsXML(strContent)) return 'endpoint';
  if (isXMLLike(strContent)) return 'xml';

  if (isValidJSON(strContent)) {
    try {
      const parsed = JSON.parse(strContent);
      if (isFindingsData(parsed)) return 'findings-list';
    } catch {
      // Continue
    }
    return 'json';
  }

  if (/(?:^|\n)\s*#{0,6}\s*Endpoint Details\b/i.test(strContent))
    return 'endpoint';
  if (/(?:^|\n)\s*#{0,6}\s*Endpoints?\s*\(/i.test(strContent))
    return 'endpoints-list';
  if (/(?:^|\n)\s*#{0,6}\s*Web Page Details\b/i.test(strContent))
    return 'web-page';
  if (/##\s*Web Pages\s*\(/i.test(strContent)) return 'web-pages-list';

  if (isMarkdown(strContent)) return 'markdown';
  if (isCode(strContent)) return 'code';

  return 'plain-text';
};

export const detectContentType = (
  content: unknown,
  toolName?: string,
): ContentType => {
  const config = getToolConfig(toolName);
  if (config.contentType) {
    return config.contentType;
  }

  return detectContentFromValue(content);
};

export const prepareToolResultData = ({
  content,
  toolName,
  input,
}: {
  content: unknown;
  toolName?: string;
  input?: unknown;
}): ToolResultData => {
  const contentType = detectContentType(content, toolName);

  return {
    content: content as string | Record<string, unknown>,
    contentType,
    toolName,
    input,
  };
};
